import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/use-auth';
import LoginPage from './pages/login';
import AuthCallbackPage from './pages/auth-callback';
import AuthMagicPage from './pages/auth-magic';
import DashboardPage from './pages/dashboard';
import ClientsListPage from './pages/clients/list';
import ClientCreatePage from './pages/clients/create';
import ClientDetailPage from './pages/clients/detail';
import BrandKitPage from './pages/clients/brand-kit';
import BrandKitsPage from './pages/brand-kits';
import ClientDocumentsPage from './pages/clients/documents';
import PortalDocumentsPage from './pages/portal/documents';
import PortalDesignsPage from './pages/portal/designs';
import TaxonomyEditorPage from './pages/clients/taxonomy';
import StrategiesListPage from './pages/strategies/list';
import StrategyCreatePage from './pages/strategies/create';
import StrategyDetailPage from './pages/strategies/detail';
import CreativesListPage from './pages/creatives/list';
import CreativeDetailPage from './pages/creatives/detail';
import MyTasksPage from './pages/my-tasks';
import CalendarPage from './pages/calendar';
import CapacityPage from './pages/capacity';
import LibraryPage from './pages/library';
import NotificationsPage from './pages/notifications';
import ChatsPage from './pages/chats';
import ClientChatsPage, { ClientChatPage } from './pages/client-chats';
import ChatThreadPage from './pages/chat-thread';
import PortalChatPage from './pages/portal/chat';
import ShootsPage from './pages/shoots';
import ShootDetailPage from './pages/shoot-detail';
import TrackerPage from './pages/tracker';
import ServicesSlaPage from './pages/sla';
import ClientUsersPage from './pages/client-users';
import ProfilePage from './pages/profile';
import InviteUserPage from './pages/admin/invite';
import UsersPage from './pages/admin/users';
import AdminPanelPage from '@/pages/admin/panel';
import UserDetailPage from './pages/admin/user-detail';
import AppShell from './components/shell/AppShell';
import RequireScope from '@/components/shell/RequireScope';
import PortalShell from './components/shell/PortalShell';
import PortalHomePage from './pages/portal/index';
import PortalCreativePage from './pages/portal/creative';
import PortalRequestsPage from './pages/portal/requests';
import PortalRequestDetailPage from './pages/portal/request-detail';
import PortalMeetingsPage from './pages/portal/meetings';
import RequestsIntakePage from './pages/requests-intake';
import RequestDetailPage from './pages/request-detail';
import UploadCenterPage from './pages/uploads';
import { UploadCenterProvider } from '@/lib/upload-center';
import UploadDock from '@/components/upload/UploadDock';
import DownloadCenterPage from './pages/downloads';
import MaterialClientsPage, { MaterialClientPage } from './pages/material';
import PortalMaterialPage from './pages/portal/material';
import PortalTrackPage from './pages/portal/track';
import PortalScriptsPage from './pages/portal/scripts';
import BookPage from './pages/book';
import ApprovePage from './pages/approve';
import ConfirmEmailPage from './pages/confirm-email';
import ConnectionsPage from './pages/connections';
import MessagesPage from './pages/messages';
import TransfersPage from './pages/transfers';
import MeetingPage from './pages/meeting';
import MeetingsPage from './pages/meetings';
import AnalyticsPage from './pages/analytics';
import { lazy, Suspense } from 'react';
// its own chunk, fetched only when a partner opens it
const PartnersPage = lazy(() => import('./pages/partners'));
import { DownloadCenterProvider } from '@/lib/download-center';
import DownloadDock from '@/components/download/DownloadDock';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted text-sm">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Client-portal accounts live in their own shell and never see staff routes.
  if (user.role === 'client') {
    // The upload queue wraps the portal too: without it, Raw material uploads fell back to
    // the dormant stand-in and silently queued 0 files. The dock links back to Raw material,
    // the only upload page a clinic login can open.
    return (
      <UploadCenterProvider>
      <PortalShell>
        <Routes>
          <Route path="/portal" element={<PortalHomePage />} />
          <Route path="/portal/material" element={<PortalMaterialPage />} />
          <Route path="/portal/track" element={<PortalTrackPage />} />
          <Route path="/portal/scripts" element={<PortalScriptsPage />} />
          <Route path="/portal/creatives/:id" element={<PortalCreativePage />} />
          <Route path="/portal/requests" element={<PortalRequestsPage />} />
          <Route path="/portal/requests/:id" element={<PortalRequestDetailPage />} />
          <Route path="/portal/meetings" element={<PortalMeetingsPage />} />
          <Route path="/portal/chat" element={<PortalChatPage />} />
          <Route path="/portal/documents" element={<PortalDocumentsPage />} />
          <Route path="/portal/designs" element={<PortalDesignsPage />} />
          <Route path="/portal/brand-kit" element={(user?.portal_scope ?? 'full') === 'full' ? <BrandKitPage portal /> : <Navigate to="/portal" replace />} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </PortalShell>
      <UploadDock to="/portal/material" />
      </UploadCenterProvider>
    );
  }
  return <>{children}</>;
}

/** Anyone but a partner lands where any unknown address lands; the server refuses them anyway. */
function PartnersGate() {
  const { user } = useAuth();
  if (!user?.partner) return <Navigate to="/" replace />;
  return <Suspense fallback={<div className="text-sm text-muted p-8 text-center">Loading...</div>}><PartnersPage /></Suspense>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/auth/magic" element={<AuthMagicPage />} />
      {/* Public: a clinic books a script call from the link it was sent. No login. */}
      <Route path="/book" element={<BookPage />} />
      {/* Public: a doctor's script approval page, and an email confirmation. No login. */}
      <Route path="/approve/:token" element={<ApprovePage />} />
      <Route path="/confirm-email/:token" element={<ConfirmEmailPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            {/* The queue lives ABOVE the router: changing page must never stop a transfer. */}
            <UploadCenterProvider>
            <DownloadCenterProvider>
            <AppShell>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/clients" element={<ClientsListPage />} />
                <Route path="/clients/new" element={<RequireScope scope="clients.write" title="New client"><ClientCreatePage /></RequireScope>} />
                <Route path="/clients/:id" element={<ClientDetailPage />} />
                <Route path="/clients/:id/brand-kit" element={<BrandKitPage />} />
                <Route path="/brand-kits" element={<BrandKitsPage />} />
                <Route path="/clients/:id/documents" element={<ClientDocumentsPage />} />
                <Route path="/clients/:id/taxonomy" element={<RequireScope scope="clients.write" title="Client taxonomy"><TaxonomyEditorPage /></RequireScope>} />
                <Route path="/strategies" element={<StrategiesListPage />} />
                <Route path="/strategies/new" element={<RequireScope scope="strategies.write" title="New strategy"><StrategyCreatePage /></RequireScope>} />
                <Route path="/strategies/:id" element={<StrategyDetailPage />} />
                <Route path="/creatives" element={<CreativesListPage />} />
                <Route path="/requests" element={<RequestsIntakePage />} />
                <Route path="/requests/:id" element={<RequestDetailPage />} />
                <Route path="/creatives/:id" element={<CreativeDetailPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/uploads" element={<UploadCenterPage />} />
                <Route path="/downloads" element={<DownloadCenterPage />} />
                <Route path="/material" element={<MaterialClientsPage />} />
                <Route path="/material/:clientId" element={<MaterialClientPage />} />
                <Route path="/tasks" element={<MyTasksPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/chats" element={<ChatsPage />} />
                <Route path="/chats/company" element={<ChatThreadPage kind="company" />} />
                <Route path="/chats/client/:id" element={<ChatThreadPage kind="client" />} />
                <Route path="/client-chats" element={<ClientChatsPage />} />
                <Route path="/client-chats/:id" element={<ClientChatPage />} />
                <Route path="/shoots" element={<ShootsPage />} />
                <Route path="/shoots/:id" element={<ShootDetailPage />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/meetings/:id" element={<MeetingPage />} />
                <Route path="/tracker" element={<TrackerPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/sla" element={<RequireScope scope="admin.panel" title="Services SLA"><ServicesSlaPage /></RequireScope>} />
                <Route path="/client-users" element={<RequireScope scope="users.read" title="Clients users"><ClientUsersPage /></RequireScope>} />
                <Route path="/connections" element={<RequireScope scope="admin.panel" title="Connections"><ConnectionsPage /></RequireScope>} />
                <Route path="/messages" element={<RequireScope scope="admin.panel" title="Message log"><MessagesPage /></RequireScope>} />
                <Route path="/transfers" element={<RequireScope scope="admin.panel" title="Transfer speeds"><TransfersPage /></RequireScope>} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/capacity" element={<RequireScope scope="tasks.read.all" title="Team capacity"><CapacityPage /></RequireScope>} />
                <Route path="/admin" element={<AdminPanelPage />} />
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/users/:id" element={<UserDetailPage />} />
                <Route path="/admin/invite" element={<InviteUserPage />} />
                <Route path="/partners" element={<PartnersGate />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
            <UploadDock />
            <DownloadDock />
            </DownloadCenterProvider>
            </UploadCenterProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
